import {
    createOrReplaceProgram,
    deleteProgram as deleteProgramApi,
    getProgram,
    getProgramCatalog,
    getProgramCatalogItem,
    hydrateProgramCatalog as hydrateProgramCatalogApi,
    upsertProgramCatalogItem,
} from '../api/programs';
import { ProgramCatalogEntry, ProgramCatalogHydrationResult, ProgramCatalogItem, ProgramResponse, ProgramWeeks } from '../api/programs.interface';
import {
    copyProgramWeekInDraft,
    createBlankProgramWeeks,
    createDefaultProgramCatalogItem,
    createProgramDraftFromResponse,
    createProgramManagementListItem,
    ensureProgramWeek,
    getProgramWeekSchedule,
    getProgramWeekNumbers,
    normalizeProgramNameInput,
    normalizeProgramSelectedWeek,
    syncProgramCreateValuesWithName,
    updateProgramCatalogItem,
    upsertProgramDayInDraft,
    validateProgramDraftBeforeSave,
    deleteProgramDayFromDraft,
} from '../components/programs/programs.utils';
import { ProgramWorkoutItemRecord } from '../api/programs.interface';
import { ProgramCreateFormValues, SaveProgramDraftResult, StartProgramDraftResult } from '../components/programs/programs.types';
import { DispatchFunction } from '../reducers';
import { addError, addSuccess } from './notifications';
import { PROGRAMS_UPDATE } from './types';

const PROGRAMS_GENERIC_ERROR_MESSAGE = 'An error occurred while working with programs.';

const resolveProgramErrorMessage = (error: any, fallbackMessage: string): string => {
    if (error?.userFriendly) {
        return error.message;
    }

    return error?.message || fallbackMessage;
};

const sortProgramItems = (items: ReturnType<typeof createProgramManagementListItem>[]) => {
    return [...items].sort((left, right) => left.sortOrder - right.sortOrder || left.displayName.localeCompare(right.displayName) || left.name.localeCompare(right.name));
};

const buildProgramManagementItems = async (): Promise<ReturnType<typeof createProgramManagementListItem>[]> => {
    const catalogEntries = await getProgramCatalog();

    return sortProgramItems(catalogEntries.map((item) => createProgramManagementListItem(item)));
};

const updatePersistedProgramList = (
    currentItems: ReturnType<typeof createProgramManagementListItem>[],
    catalogItem: ProgramCatalogEntry,
) => {
    const nextItem = createProgramManagementListItem(catalogItem);
    const remainingItems = currentItems.filter((item) => item.name !== catalogItem.key);

    return sortProgramItems([...remainingItems, nextItem]);
};

const buildProgramDraftCatalog = (program: ProgramResponse, catalogItem?: ProgramCatalogEntry | ProgramCatalogItem): ProgramCatalogItem => {
    if (catalogItem) {
        return {
            completionAction: catalogItem.completionAction,
            description: catalogItem.description,
            displayName: catalogItem.displayName,
            imageUrl: catalogItem.imageUrl,
            visibility: catalogItem.visibility,
            live: catalogItem.live,
            nextProgramKey: catalogItem.nextProgramKey,
            sortOrder: catalogItem.sortOrder,
            weeks: catalogItem.weeks,
        };
    }

    return createDefaultProgramCatalogItem(program.name, getProgramWeekNumbers(program.weeks).length || 1);
};

export const loadPrograms = () => async (dispatch: DispatchFunction) => {
    dispatch({ type: PROGRAMS_UPDATE, payload: { loadingList: true } });

    try {
        const items = await buildProgramManagementItems();

        dispatch({ type: PROGRAMS_UPDATE, payload: { items, loadingList: false } });
    } catch (error: any) {
        dispatch(addError(resolveProgramErrorMessage(error, PROGRAMS_GENERIC_ERROR_MESSAGE)));
        dispatch({ type: PROGRAMS_UPDATE, payload: { items: [], loadingList: false } });
    }
};

export const loadProgram = (name: string, preferredWeek?: number) => async (dispatch: DispatchFunction) => {
    dispatch({ type: PROGRAMS_UPDATE, payload: { loadingProgram: true, selectedProgramName: name } });

    try {
        const [item, catalogItem] = await Promise.all([
            getProgram(name),
            getProgramCatalogItem(name).catch(() => undefined),
        ]);

        dispatch({
            type: PROGRAMS_UPDATE,
            payload: {
                hasUnsavedChanges: false,
                loadingProgram: false,
                selectedProgramCatalog: catalogItem,
                selectedProgram: item,
                selectedProgramName: item.name,
                selectedWeek: normalizeProgramSelectedWeek(item.weeks, preferredWeek),
            },
        });
    } catch (error: any) {
        dispatch(addError(resolveProgramErrorMessage(error, 'Unable to load the program.')));
        dispatch({
            type: PROGRAMS_UPDATE,
            payload: {
                draftProgram: undefined,
                hasUnsavedChanges: false,
                loadingProgram: false,
                selectedProgramCatalog: undefined,
                selectedProgram: undefined,
                selectedProgramName: name,
            },
        });
    }
};

export const loadProgramEditor = (name: string, preferredWeek?: number) => async (dispatch: DispatchFunction) => {
    dispatch({ type: PROGRAMS_UPDATE, payload: { loadingProgram: true, selectedProgramName: name } });

    try {
        const [item, catalogItem] = await Promise.all([
            getProgram(name),
            getProgramCatalogItem(name).catch(() => undefined),
        ]);
        const draftCatalog = buildProgramDraftCatalog(item, catalogItem);

        dispatch({
            type: PROGRAMS_UPDATE,
            payload: {
                draftProgram: createProgramDraftFromResponse(item, draftCatalog),
                hasUnsavedChanges: false,
                loadingProgram: false,
                selectedProgramCatalog: catalogItem,
                selectedProgram: item,
                selectedProgramName: item.name,
                selectedWeek: normalizeProgramSelectedWeek(item.weeks, preferredWeek),
            },
        });
    } catch (error: any) {
        dispatch(addError(resolveProgramErrorMessage(error, 'Unable to load the program editor.')));
        dispatch({
            type: PROGRAMS_UPDATE,
            payload: {
                draftProgram: undefined,
                hasUnsavedChanges: false,
                loadingProgram: false,
                selectedProgramCatalog: undefined,
                selectedProgram: undefined,
                selectedProgramName: name,
            },
        });
    }
};

export const clearProgramDraft = () => async (dispatch: DispatchFunction) => {
    dispatch({
        type: PROGRAMS_UPDATE,
        payload: {
            draftProgram: undefined,
            hasUnsavedChanges: false,
            selectedProgramCatalog: undefined,
            selectedProgram: undefined,
            selectedProgramName: undefined,
            selectedWeek: 1,
        },
    });
};

export const startNewProgramDraft = (
    values: ProgramCreateFormValues,
) => async (dispatch: DispatchFunction): Promise<StartProgramDraftResult> => {
    const normalizedName = normalizeProgramNameInput(values.name);

    try {
        let weeks: ProgramWeeks = createBlankProgramWeeks();

        if (values.templateType === 'copyProgram') {
            const sourceProgram = await getProgram(values.sourceProgramName);

            weeks = sourceProgram.weeks;
        }

        if (values.templateType === 'copyWeek') {
            const sourceProgram = await getProgram(values.sourceProgramName);
            const sourceWeek = Number(values.sourceWeek);
            const sourceSchedule = getProgramWeekSchedule(sourceProgram.weeks, sourceWeek);

            if (!Object.keys(sourceSchedule).length) {
                throw new Error('The selected source week is empty.');
            }

            weeks = copyProgramWeekInDraft({ '1': {} }, sourceWeek, 1);
            weeks['1'] = Object.entries(sourceSchedule).reduce((accumulator, [dayKey, item]) => {
                accumulator[dayKey] = {
                    ...item,
                    week: 1,
                };

                return accumulator;
            }, {} as ProgramWeeks['1']);
        }

        const parsedWeeks = Number(values.weeks);
        const parsedSortOrder = Number(values.sortOrder);
        const draftCatalog = createDefaultProgramCatalogItem(normalizedName, parsedWeeks);

        draftCatalog.displayName = values.displayName.trim() || draftCatalog.displayName;
        draftCatalog.description = values.description.trim() || draftCatalog.description;
        draftCatalog.imageUrl = values.imageUrl.trim() || undefined;
        draftCatalog.visibility = values.visibility;
        draftCatalog.live = values.visibility === 'live';
        draftCatalog.nextProgramKey = values.completionAction === 'advance'
            ? normalizeProgramNameInput(values.nextProgramKey) || undefined
            : undefined;
        draftCatalog.completionAction = values.completionAction;
        draftCatalog.sortOrder = parsedSortOrder;
        draftCatalog.weeks = parsedWeeks;

        dispatch({
            type: PROGRAMS_UPDATE,
            payload: {
                draftProgram: {
                    catalog: draftCatalog,
                    name: normalizedName,
                    weeks,
                },
                hasUnsavedChanges: true,
                selectedProgram: undefined,
                selectedProgramName: normalizedName,
                selectedWeek: 1,
            },
        });

        return { success: true };
    } catch (error: any) {
        const message = resolveProgramErrorMessage(error, 'Unable to start the new program draft.');

        dispatch(addError(message));

        return {
            message,
            success: false,
        };
    }
};

export const setProgramSelectedWeek = (week: number) => async (dispatch: DispatchFunction) => {
    dispatch({ type: PROGRAMS_UPDATE, payload: { selectedWeek: week } });
};

export const startImportedProgramDraft = (name: string, weeks: ProgramWeeks) => async (dispatch: DispatchFunction) => {
    const normalizedName = normalizeProgramNameInput(name);
    const totalWeeks = getProgramWeekNumbers(weeks).length || 1;

    dispatch({
        type: PROGRAMS_UPDATE,
        payload: {
            draftProgram: {
                catalog: createDefaultProgramCatalogItem(normalizedName, totalWeeks),
                name: normalizedName,
                weeks,
            },
            hasUnsavedChanges: true,
            selectedProgram: undefined,
            selectedProgramName: normalizedName,
            selectedWeek: normalizeProgramSelectedWeek(weeks),
        },
    });
};

export const addProgramDraftWeek = (week: number) => async (dispatch: DispatchFunction, getState: any) => {
    const draftProgram = getState().programs.draftProgram;

    if (!draftProgram) {
        return;
    }

    dispatch({
        type: PROGRAMS_UPDATE,
        payload: {
            draftProgram: {
                ...draftProgram,
                weeks: ensureProgramWeek(draftProgram.weeks, week),
            },
            hasUnsavedChanges: true,
            selectedWeek: week,
        },
    });
};

export const updateDraftProgramDay = (
    week: number,
    day: number,
    item: ProgramWorkoutItemRecord,
) => async (dispatch: DispatchFunction, getState: any) => {
    const draftProgram = getState().programs.draftProgram;

    if (!draftProgram) {
        return;
    }

    dispatch({
        type: PROGRAMS_UPDATE,
        payload: {
            draftProgram: {
                ...draftProgram,
                weeks: upsertProgramDayInDraft(draftProgram.weeks, week, day, item),
            },
            hasUnsavedChanges: true,
            selectedWeek: week,
        },
    });
};

export const removeDraftProgramDay = (week: number, day: number) => async (dispatch: DispatchFunction, getState: any) => {
    const draftProgram = getState().programs.draftProgram;

    if (!draftProgram) {
        return;
    }

    dispatch({
        type: PROGRAMS_UPDATE,
        payload: {
            draftProgram: {
                ...draftProgram,
                weeks: deleteProgramDayFromDraft(draftProgram.weeks, week, day),
            },
            hasUnsavedChanges: true,
            selectedWeek: week,
        },
    });
};

export const copyDraftProgramWeek = (
    sourceWeek: number,
    targetWeek: number,
) => async (dispatch: DispatchFunction, getState: any) => {
    const draftProgram = getState().programs.draftProgram;

    if (!draftProgram) {
        return;
    }

    dispatch({
        type: PROGRAMS_UPDATE,
        payload: {
            draftProgram: {
                ...draftProgram,
                weeks: copyProgramWeekInDraft(draftProgram.weeks, sourceWeek, targetWeek),
            },
            hasUnsavedChanges: true,
            selectedWeek: targetWeek,
        },
    });
};

export const discardDraftProgramChanges = () => async (dispatch: DispatchFunction, getState: any) => {
    const selectedProgram = getState().programs.selectedProgram as ProgramResponse | undefined;
    const selectedProgramCatalog = getState().programs.selectedProgramCatalog as ProgramCatalogEntry | undefined;

    dispatch({
        type: PROGRAMS_UPDATE,
        payload: {
            draftProgram: selectedProgram ? createProgramDraftFromResponse(selectedProgram, buildProgramDraftCatalog(selectedProgram, selectedProgramCatalog)) : undefined,
            hasUnsavedChanges: false,
            selectedWeek: selectedProgram ? normalizeProgramSelectedWeek(selectedProgram.weeks, getState().programs.selectedWeek) : 1,
        },
    });
};

export const updateDraftProgramCatalog = (
    fieldName: keyof ProgramCatalogItem,
    value: boolean | number | string,
) => async (dispatch: DispatchFunction, getState: any) => {
    const draftProgram = getState().programs.draftProgram;

    if (!draftProgram) {
        return;
    }

    dispatch({
        type: PROGRAMS_UPDATE,
        payload: {
            draftProgram: {
                ...draftProgram,
                catalog: updateProgramCatalogItem(draftProgram.catalog, fieldName, value),
            },
            hasUnsavedChanges: true,
        },
    });
};

export const saveDraftProgram = () => async (dispatch: DispatchFunction, getState: any): Promise<SaveProgramDraftResult> => {
    const draftProgram = getState().programs.draftProgram;

    if (!draftProgram) {
        return {
            message: 'There is no draft program to save.',
            success: false,
        };
    }

    const validationIssues = validateProgramDraftBeforeSave(draftProgram);

    if (validationIssues.length) {
        const message = validationIssues.length === 1
            ? validationIssues[0]
            : `Cannot save program until ${validationIssues.length} issues are fixed. ${validationIssues[0]}`;

        dispatch(addError(message));

        return {
            message,
            success: false,
        };
    }

    dispatch({ type: PROGRAMS_UPDATE, payload: { savingProgram: true } });

    try {
        const catalogItem = await upsertProgramCatalogItem(draftProgram.name, draftProgram.catalog);
        const item = await createOrReplaceProgram(draftProgram.name, draftProgram.weeks);
        const currentItems = getState().programs.items as ReturnType<typeof createProgramManagementListItem>[];
        const draftCatalog = buildProgramDraftCatalog(item, catalogItem);

        dispatch({
            type: PROGRAMS_UPDATE,
            payload: {
                draftProgram: createProgramDraftFromResponse(item, draftCatalog),
                hasUnsavedChanges: false,
                items: updatePersistedProgramList(currentItems, catalogItem),
                savingProgram: false,
                selectedProgramCatalog: catalogItem,
                selectedProgram: item,
                selectedProgramName: item.name,
                selectedWeek: normalizeProgramSelectedWeek(item.weeks, getState().programs.selectedWeek),
            },
        });
        dispatch(addSuccess('Program saved successfully.'));

        return {
            catalogItem,
            item,
            success: true,
        };
    } catch (error: any) {
        const message = resolveProgramErrorMessage(error, 'Unable to save the program.');

        dispatch(addError(message));
        dispatch({ type: PROGRAMS_UPDATE, payload: { savingProgram: false } });

        return {
            message,
            success: false,
        };
    }
};

export const deleteProgram = (name: string) => async (dispatch: DispatchFunction, getState: any): Promise<{ message?: string; success: boolean }> => {
    dispatch({ type: PROGRAMS_UPDATE, payload: { deletingProgramName: name } });

    try {
        await deleteProgramApi(name);
        const currentItems = getState().programs.items as ReturnType<typeof createProgramManagementListItem>[];

        dispatch({
            type: PROGRAMS_UPDATE,
            payload: {
                deletingProgramName: undefined,
                draftProgram: getState().programs.draftProgram?.name === name ? undefined : getState().programs.draftProgram,
                hasUnsavedChanges: getState().programs.draftProgram?.name === name ? false : getState().programs.hasUnsavedChanges,
                items: currentItems.filter((item) => item.name !== name),
                selectedProgramCatalog: getState().programs.selectedProgramName === name ? undefined : getState().programs.selectedProgramCatalog,
                selectedProgram: getState().programs.selectedProgram?.name === name ? undefined : getState().programs.selectedProgram,
                selectedProgramName: getState().programs.selectedProgramName === name ? undefined : getState().programs.selectedProgramName,
            },
        });
        dispatch(addSuccess('Program deleted successfully.'));

        return { success: true };
    } catch (error: any) {
        const message = resolveProgramErrorMessage(error, 'Unable to delete the program.');

        dispatch(addError(message));
        dispatch({ type: PROGRAMS_UPDATE, payload: { deletingProgramName: undefined } });

        return {
            message,
            success: false,
        };
    }
};

const buildProgramCatalogHydrationMessage = (result: ProgramCatalogHydrationResult): string => {
    const summary = [
        `${result.createdKeys.length} created`,
        `${result.updatedKeys.length} updated`,
        `${result.skippedKeys.length} skipped`,
    ].join(', ');

    return `Program catalog hydrated successfully. ${summary}.`;
};

export const hydrateProgramCatalog = (overwriteExisting = false) => async (
    dispatch: DispatchFunction,
    getState: any,
): Promise<{ message?: string; success: boolean }> => {
    dispatch({ type: PROGRAMS_UPDATE, payload: { hydratingProgramCatalog: true } });

    try {
        const result = await hydrateProgramCatalogApi(overwriteExisting);
        const items = await buildProgramManagementItems();

        dispatch({
            type: PROGRAMS_UPDATE,
            payload: {
                hydratingProgramCatalog: false,
                items,
            },
        });
        dispatch(addSuccess(buildProgramCatalogHydrationMessage(result)));

        return { success: true };
    } catch (error: any) {
        const message = resolveProgramErrorMessage(error, 'Unable to hydrate the program catalog.');

        dispatch(addError(message));
        dispatch({ type: PROGRAMS_UPDATE, payload: { hydratingProgramCatalog: false } });

        return {
            message,
            success: false,
        };
    }
};